import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import moment from 'moment'
import { nanoid } from 'nanoid'

import { FieldsErrorType, ICardListener, ICardListenerContainer, ICardListenerTabFiles, ICardListenerTabPrimary, IFieldsError, IFilesList } from './types'
import { CardListener } from './ui'

import { useValue } from '@hooks/useValue'
import { useCardSaveGuard } from '@hooks/useCardSaveGuard'
import { useDebounce } from '@hooks/useDebounce'
import { phoneMask, validFormData } from '@hooks/useForm'
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'

import CommonService from '@services/common.service'
import { reportUnexpectedClientError } from '@services/client-error.service'

import { useCommon } from '@api/common/state'
import { useListeners } from '@api/listeners/state'
import { IListenerCard } from '@api/listeners/state.types'
import { abandonListenerDraft, createListenerDraft, deleteListenerDraftFile, getDocumentExtractionConfig, uploadListenerDraft } from '@api/listeners/document-extraction'

import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'
import { IModalNotice, ModalNotice } from '@primitive/modal.notice'
import { IModalPreview, ModalPreview } from '@primitive/modal.preview'
import { CardModeType } from '@smart/common.type'
import { DictNameType } from '@smart/dictSelector/types'

import { CardListenerTabLogs } from './ui.tab.logs'
import { CardListenerTabFiles } from './ui.tab.files'
import { CardListenerTabPrimary } from './ui.tab.primary'
import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'
import { ListenerDocumentExtraction } from './ui.extraction'
import type { ExtractionSelection } from './ui.extraction'
import type { ListenerNames, RecognizedNames } from './recognized-names'


type ListenerFormState = {
    citizenshipID: number | ''
    snils: string
    phoneNumber: string
    email: string
    lastName: string
    firstName: string
    secondName: string
    birthDate: string
    fullNameFrom: string
    sex: string
    passport: string
    registration: string
    educationLevelID: number | ''
    maidenName: string
    diplom: string
    comment: string
    lkLogin: string
    pendingFiles: string[]
}

const DEFAULT_LISTENER_FORM_STATE: ListenerFormState = {
    citizenshipID: 185,
    snils: '',
    phoneNumber: '',
    email: '',
    lastName: '',
    firstName: '',
    secondName: '',
    birthDate: '',
    fullNameFrom: '',
    sex: '',
    passport: '',
    registration: '',
    educationLevelID: 3,
    maidenName: '',
    diplom: '',
    comment: '',
    lkLogin: '',
    pendingFiles: [],
}

const createListenerFormSnapshot = (state: ListenerFormState) => JSON.stringify(state)
const createListenerNamesSignature = (lastName: string, firstName: string, secondName: string) => JSON.stringify([lastName, firstName, secondName])

const getListenerFormState = (listener: IListenerCard): ListenerFormState => ({
    citizenshipID: listener.citizenshipID || '',
    snils: listener.snils || '',
    phoneNumber: listener.phoneNumber || '',
    email: listener.email || '',
    lastName: listener.lastName || '',
    firstName: listener.firstName || '',
    secondName: listener.secondName || '',
    birthDate: listener.birthDate || '',
    fullNameFrom: listener.fullNameFrom || '',
    sex: listener.sex || '',
    passport: listener.passport || '',
    registration: listener.registration || '',
    educationLevelID: listener.educationLevelID || '',
    maidenName: listener.maidenName || '',
    diplom: listener.diplom || '',
    comment: listener.comment || '',
    lkLogin: listener.lkLogin || '',
    pendingFiles: [],
})


/** Карточка по слушателю - Контейнер */
export const CardListenerContainer = ({isOpen, cardMode, cardID, handlerButtonOk, handlerButtonCancel}: ICardListenerContainer) => {
    const notify = useContext(NotificationContext)

    const filesListSendToSrv = useValue<File[]>([])
    const filesList = useValue<IFilesList[]>([])
    const [extractionFiles, setExtractionFiles] = useState<ExtractionSelection[]>([])
    const activeExtractionRef = useRef<ExtractionSelection[] | null>(null)
    const [isRecognitionLoading, setIsRecognitionLoading] = useState(false)
    const recognitionRequestRef = useRef<AbortController | null>(null)
    const recognitionSessionRef = useRef(0)
    const draftIDRef = useRef<string>()
    const stagedFilesRef = useRef(new Map<string, number>())
    const uncertainDraftUploadsRef = useRef(new Set<string>())
    const savingRef = useRef(false)

    const discardDraft = () => {
        const draftID = draftIDRef.current
        draftIDRef.current = undefined
        stagedFilesRef.current.clear()
        uncertainDraftUploadsRef.current.clear()
        if (draftID && !savingRef.current) void abandonListenerDraft(draftID).catch(() => undefined)
    }

    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const modalNotice = useValue<IModalNotice>({isOpen: false})
    const modalPreview = useValue<IModalPreview>({isOpen: false})

    const tabActive = useValue<string>('primary')
    const fieldsError = useValue<IFieldsError>({})
    const currentCardID = useValue<number>(0)
    const currentCardMode = useValue<CardModeType>('new')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormSnapshotRef = useRef(createListenerFormSnapshot(DEFAULT_LISTENER_FORM_STATE))
    const savedNamesSignatureRef = useRef(createListenerNamesSignature('', '', ''))
    const hasSavedChangesRef = useRef(false)
    const saveGuard = useCardSaveGuard(isOpen, `${cardMode}:${cardID}`, () => {
        hasSavedChangesRef.current = true
        notify && notifyOpen('Результат сохранения не подтверждён. Анкета могла быть создана. Повторное создание заблокировано; закройте анкету и проверьте список.', 'warning', 10000, notify)
    })
    const isFormReadyRef = useRef(false)


    const listenerNumber = useValue<number | ''>('')
    const listenerDateCreate = useValue<string>('')
    const clientSnilsAuto = useValue<boolean>(false)

    const txtCitizenshipID = useValue<number | ''>(185)
    const txtCitizenship = useValue<string>('643 - РОССИЯ')
    const txtSnilsBuff = useValue<string>('') // Буффер, оригинальный СЛИЛС
    const txtSnils = useValue<string>('')
    const txtClientPhone = useValue<string>('')
    const txtClientEmail = useValue<string>('')
    
    const txtSex = useValue<string>('')
    const txtBirthDate = useValue<string>('')
    const txtFullNameFrom = useValue<string>('')
    
    const txtPassport = useValue<string>('')
    const txtAddress = useValue<string>('')

    const txtEducationLevelID = useValue<number | ''>(3)
    const txtEducationLevel = useValue<string>('Высшее образование')
    const txtLastNameDiplom = useValue<string>('')
    const txtDiplom = useValue<string>('')
    
    const txtComment = useValue<string>('')
    const txtLogin = useValue<string>('')

    //=================================================================================

    const {
        isLoadingCard, actionGetListenerCard, actionSetListenerCard, actionGetListenerCheckPhone, 
        actionGetListenerCheckSnils, actionSetListenerDeleteFiles, actionSetListenerUploadFiles, actionGetListenerFiles
    } = useListeners(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        actionGetListenerCard: state.actionGetListenerCard,
        actionSetListenerCard: state.actionSetListenerCard,
        actionGetListenerCheckPhone: state.actionGetListenerCheckPhone,
        actionGetListenerCheckSnils: state.actionGetListenerCheckSnils,
        actionSetListenerDeleteFiles: state.actionSetListenerDeleteFiles,
        actionSetListenerUploadFiles: state.actionSetListenerUploadFiles,
        actionGetListenerFiles: state.actionGetListenerFiles,
    })))

    const { getDeclinationString } = useCommon(useShallow((state) => ({
        getDeclinationString: state.actionGetDeclinationString,
    })))

    //=================================================================================

    /** Фамилия контрагента */
    const [txtLastName, setTxtLastName] = useState<string>('')
    /** Имя контрагента */
    const [txtFirstName, setTxtFirstName] = useState<string>('')
    /** Отчество контрагента */
    const [txtSecondName, setTxtSecondName] = useState<string>('')

    const getCurrentFormSnapshot = (pendingFiles: File[] = filesListSendToSrv.value) => createListenerFormSnapshot({
        citizenshipID: txtCitizenshipID.value,
        snils: txtSnils.value,
        phoneNumber: txtClientPhone.value,
        email: txtClientEmail.value,
        lastName: txtLastName,
        firstName: txtFirstName,
        secondName: txtSecondName,
        birthDate: txtBirthDate.value,
        fullNameFrom: txtFullNameFrom.value,
        sex: txtSex.value,
        passport: txtPassport.value,
        registration: txtAddress.value,
        educationLevelID: txtEducationLevelID.value,
        maidenName: txtLastNameDiplom.value,
        diplom: txtDiplom.value,
        comment: txtComment.value,
        lkLogin: txtLogin.value,
        pendingFiles: pendingFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`),
    })

    const applyListenerCardData = (listener: IListenerCard) => {
        const formState = getListenerFormState(listener)

        currentCardID.setValue(listener.listenerID)
        currentCardMode.setValue('edit')
        listenerNumber.setValue(listener.listenerID)
        listenerDateCreate.setValue(moment(listener.dateCreate).format('DD.MM.YYYY'))
        clientSnilsAuto.setValue(listener.snils.length > 14)

        txtCitizenshipID.setValue(formState.citizenshipID)
        txtCitizenship.setValue(`${listener.citizenshipCode} - ${listener.citizenshipName}`)
        txtSnilsBuff.setValue(formState.snils)
        txtSnils.setValue(formState.snils)
        txtClientPhone.setValue(formState.phoneNumber)
        txtClientEmail.setValue(formState.email)

        txtSex.setValue(formState.sex)
        setTxtLastName(formState.lastName)
        setTxtFirstName(formState.firstName)
        setTxtSecondName(formState.secondName)
        txtBirthDate.setValue(formState.birthDate)
        txtFullNameFrom.setValue(formState.fullNameFrom)

        txtPassport.setValue(formState.passport)
        txtAddress.setValue(formState.registration)

        txtEducationLevelID.setValue(formState.educationLevelID)
        txtEducationLevel.setValue(listener.educationLevelName || '')
        txtLastNameDiplom.setValue(formState.maidenName)
        txtDiplom.setValue(formState.diplom)

        txtComment.setValue(formState.comment)
        txtLogin.setValue(formState.lkLogin)
        filesListSendToSrv.setValue([])
        filesList.setValue(listener.filesList.map((item) => ({
            fileID: item.listenerFileID,
            fileName: item.fileName,
            filePath: item.filePath,
            fileSize: item.fileSize,
            dateCreate: item.dateCreate,
            isSelected: false
        })))

        savedFormSnapshotRef.current = createListenerFormSnapshot(formState)
        savedNamesSignatureRef.current = createListenerNamesSignature(formState.lastName, formState.firstName, formState.secondName)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        if (!isOpen) return

        const namesSignature = createListenerNamesSignature(txtLastName, txtFirstName, txtSecondName)
        if (namesSignature === savedNamesSignatureRef.current) return

        const lastName = txtLastName?.trim().charAt(0).toUpperCase() + txtLastName?.trim().toLowerCase().slice(1)
        const firstName = txtFirstName?.trim().charAt(0).toUpperCase() + txtFirstName?.trim().toLowerCase().slice(1)
        const secondName = txtSecondName?.trim().charAt(0).toUpperCase() + txtSecondName?.trim().toLowerCase().slice(1)
        const fullName = `${lastName} ${firstName} ${secondName}`.trim()
        if (!fullName) return
        let isCurrentRequest = true
        const timeoutID = setTimeout(() => {
            getDeclinationString(fullName).then((res) => {
                if (isCurrentRequest && res.data?.result) txtFullNameFrom.setValue(res.data.result)
            }).catch((res) => {
                reportUnexpectedClientError(res, 'CardListener.getDeclinationString')
                if (isCurrentRequest) notify && notifyOpen(res.message, 'error', 1500, notify)
            })
        }, 2000)

        return () => {
            isCurrentRequest = false
            clearTimeout(timeoutID)
        }
    }, [isOpen, txtLastName, txtFirstName, txtSecondName]) // eslint-disable-line react-hooks/exhaustive-deps

    //=================================================================================

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            currentCardID.setValue(Number(cardID) || 0)
            currentCardMode.setValue(cardMode || 'new')

            if (cardMode === 'new') {
                isFormReadyRef.current = true
            }

            cardMode === 'edit' && actionGetListenerCard(Number(cardID)).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyListenerCardData(res)
            })

        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
            recognitionSessionRef.current += 1
            activeExtractionRef.current = null
            recognitionRequestRef.current?.abort()
            recognitionRequestRef.current = null
            discardDraft()
        }
    }, [isOpen, cardID, cardMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = getCurrentFormSnapshot()
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshotRef.current)
    }, [isOpen, currentFormSnapshot, setIsDisableBtnSave])

    // useEffect(() => {
    //     if (cardMode === 'new' && (txtLastName.value || txtFirstName.value || txtSecondName.value)) {
    //         const lastName = txtLastName.value?.trim().charAt(0).toUpperCase() + txtLastName.value?.trim().toLowerCase().slice(1)
    //         const firstName = txtFirstName.value?.trim().charAt(0).toUpperCase() + txtFirstName.value?.trim().toLowerCase().slice(1)
    //         const secondName = txtSecondName.value?.trim().charAt(0).toUpperCase() + txtSecondName.value?.trim().toLowerCase().slice(1)

    //         let newFullNameFrom = `${CommonService.declensionFullName(lastName, firstName, secondName, 'кого/чего')}`
    //         txtFullNameFrom.setValue(newFullNameFrom.trim())
    //     }
    // }, [txtLastName.value, txtFirstName.value, txtSecondName.value]) // eslint-disable-line react-hooks/exhaustive-deps

    //=================================================================================

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        recognitionSessionRef.current += 1
        activeExtractionRef.current = null
        recognitionRequestRef.current?.abort()
        recognitionRequestRef.current = null
        discardDraft()
        setIsRecognitionLoading(false)
        setExtractionFiles([])
        tabActive.setValue('primary')
        fieldsError.setValue({})
        currentCardID.setValue(0)
        currentCardMode.setValue('new')
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        savedFormSnapshotRef.current = createListenerFormSnapshot(DEFAULT_LISTENER_FORM_STATE)
        savedNamesSignatureRef.current = createListenerNamesSignature('', '', '')

        listenerNumber.setValue('')
        listenerDateCreate.setValue('')
        clientSnilsAuto.setValue(false)

        txtCitizenshipID.setValue(185)
        txtCitizenship.setValue('643 - РОССИЯ')
        txtSnilsBuff.setValue('') // Буффер, оригинальный СЛИЛС
        txtSnils.setValue('')
        txtClientPhone.setValue('')
        txtClientEmail.setValue('')

        txtSex.setValue('')
        setTxtLastName('')
        setTxtFirstName('')
        setTxtSecondName('')
        txtBirthDate.setValue('')
        txtFullNameFrom.setValue('')

        txtPassport.setValue('')
        txtAddress.setValue('')

        txtEducationLevelID.setValue(3)
        txtEducationLevel.setValue('Высшее образование')
        txtLastNameDiplom.setValue('')
        txtDiplom.setValue('')

        txtComment.setValue('')
        txtLogin.setValue('')

        filesList.setValue([])
        filesListSendToSrv.setValue([])
    }

    const getCardPage = (tabActive: string) => {
        switch (tabActive) {
            case 'primary': return <CardListenerTabPrimary {...propsToTabPrimary} />
            case 'files': return <CardListenerTabFiles {...propsToTabFiles} />
            case 'logs': return <CardListenerTabLogs />
        }
    }

    /** Функция по генерации СНИЛС */
    const handlerSnilsAuto = (checked: boolean) => {
        clientSnilsAuto.setValue(checked)

        if (checked) {handleChangeSnils(nanoid())} 
        else {
            let snilsFromBuffer: string = ''
            if (txtSnilsBuff.value.length === 14) snilsFromBuffer = txtSnilsBuff.value

            txtSnils.setValue(snilsFromBuffer || '')
        }
    }
    /** Функция по генерации номера телефона */
    const handlerPhoneGenerate = () => {
        handleChangePhone(`234${CommonService.generateDigits(17)}`)
    }



    /** Проверка по номеру телефона, существование лушателя */
    const listenerCheckPhoneNumber = async (phoneNumber: string) => {
        const res = await actionGetListenerCheckPhone(currentCardID.value, phoneNumber)
        if (res?.result) handlerOpenModalNotice(true, 'Извещение...', `<b>Внимание!</b><br/>Слушатель с таким номером телефона уже зарегистрирован, идентификатор слушателя: <b>${res.appID}</b>`)
    }
    /** Проверка по СНИЛС, существование лушателя */
    const listenerCheckSnils = async (snils: string) => {
        const res = await actionGetListenerCheckSnils(currentCardID.value, snils)
        if (res?.result) {
            const listenerReference = Number.isSafeInteger(res.appID) && Number(res.appID) > 0
                ? `, идентификатор слушателя: <b>${res.appID}</b>` : ''
            handlerOpenModalNotice(true, 'Извещение...', `<b>Внимание!</b><br/>Слушатель с таким СНИЛС уже зарегистрирован${listenerReference}`)
        }
    }

    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string}) => {
        switch (dictName) {
            case 'citizenship': {
                txtCitizenshipID.setValue(Number(data.id))
                txtCitizenship.setValue(data.name)
                break
            }

            case 'educationLevel': {
                txtEducationLevelID.setValue(Number(data.id))
                txtEducationLevel.setValue(data.name)
                break
            }
        }
    }

    //=================================================================================

    /** Открытие/закрытие окна извещения */
    const handlerOpenModalNotice = (isOpen: boolean, headerText: string, contentText: string) => {
        modalNotice.setValue({
            isOpen, headerText, contentText, 
            handleButtonOk() {modalNotice.setValue({isOpen: false})}, 
        })
    }
    const closeCard = () => {
        activeExtractionRef.current = null
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handlerButtonOk && handlerButtonOk()
        } else {
            handlerButtonCancel && handlerButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            cancelButtonText: 'Продолжить редактирование',
            okButtonText: 'Закрыть без сохранения',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }
    /** Открытие/закрытие окна предпросмотра документа */
    const handlerOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }


    /** Функция по выделению файлов */
    const handlerSelectedFiles = (fileID: number) => {
        const newList = filesList.value.map(item => {
            if (item.fileID === fileID) {
                return { ...item, isSelected: !item.isSelected }
            }

            return item
        })

        filesList.setValue(newList)
    }
    /** Функция по выделению всех файлов */
    const handlerSelectedFilesAll = () => {
        const selectAll = !filesList.value.every(item => item.isSelected)
        const newList = filesList.value.map(item => {
            return { ...item, isSelected: selectAll }
        })

        filesList.setValue(newList)
    }
    const openExtraction = (files: ExtractionSelection[]) => {
        activeExtractionRef.current = files
        setExtractionFiles(files)
    }
    const closeExtraction = () => {
        activeExtractionRef.current = null
        setExtractionFiles([])
    }
    const handlerRecognizeFiles = async () => {
        if (!isOpen || isLoadingCard || savingRef.current || saveGuard.isBlocked || extractionFiles.length || recognitionRequestRef.current) return
        const selected = filesList.value.filter(item => item.isSelected)
        if (!selected.length) return
        const controller = new AbortController()
        const session = recognitionSessionRef.current
        const listenerID = currentCardID.value
        recognitionRequestRef.current = controller
        setIsRecognitionLoading(true)
        try {
            const response = await getDocumentExtractionConfig(listenerID, controller.signal)
            if (controller.signal.aborted || session !== recognitionSessionRef.current) return
            if (response.status !== 200 || typeof response.data?.enabled !== 'boolean') {
                notify && notifyOpen('Не удалось проверить доступность распознавания документов', 'error', 5000, notify)
                return
            }
            if (!response.data.enabled) {
                notify && notifyOpen('Распознавание LMS временно недоступно. Повторите позже.', 'error', 5000, notify)
                return
            }
            if (selected.length > 10) {
                notify && notifyOpen('Для распознавания можно выбрать не более 10 документов', 'error', 5000, notify)
                return
            }
            if (currentCardMode.value === 'new') {
                const originals = selected.map(item => filesListSendToSrv.value.find(file => file.name === item.fileName))
                if (originals.some(file => !file || file.size > 10 * 1024 * 1024 || !/\.(jpe?g|png|webp|pdf)$/i.test(file.name))) {
                    notify && notifyOpen('Для распознавания выберите JPEG, PNG, WebP или PDF размером не более 10 МБ каждый.', 'error', 5000, notify)
                    return
                }
                if (!draftIDRef.current) {
                    const created = await createListenerDraft()
                    const draftID = created.data?.draftID
                    if (controller.signal.aborted || session !== recognitionSessionRef.current) {
                        if (draftID) void abandonListenerDraft(draftID).catch(() => undefined)
                        return
                    }
                    if (created.status !== 200 || !draftID) throw new Error('draft_create_failed')
                    draftIDRef.current = draftID
                }
                const pending = (originals as File[]).filter(file => !stagedFilesRef.current.has(file.name))
                if (pending.length) {
                    pending.forEach(file => uncertainDraftUploadsRef.current.add(file.name))
                    const uploaded = await uploadListenerDraft(draftIDRef.current, pending, controller.signal)
                    if (controller.signal.aborted || session !== recognitionSessionRef.current) return
                    const uploadedFiles = uploaded.data?.files
                    if (uploaded.status !== 200 || !uploadedFiles || uploadedFiles.length !== pending.length || uploadedFiles.some(file => !Number.isSafeInteger(file.fileID) || file.fileID <= 0)) {
                        notify && notifyOpen(uploaded.message || 'Не удалось загрузить документы в черновик. Повторите загрузку.', 'error', 5000, notify)
                        return
                    }
                    pending.forEach((original, index) => {
                        stagedFilesRef.current.set(original.name, uploadedFiles[index].fileID)
                        uncertainDraftUploadsRef.current.delete(original.name)
                    })
                }
                if (originals.some(file => !stagedFilesRef.current.has(file!.name))) throw new Error('draft_upload_incomplete')
                openExtraction((originals as File[]).map(original => ({ fileID: stagedFilesRef.current.get(original.name)!, name: original.name, url: '', original })))
                return
            }
            if (listenerID <= 0 || selected.some(item => !Number.isInteger(Number(item.fileID)) || Number(item.fileID) <= 0 || !item.filePath)) {
                notify && notifyOpen('Не удалось найти сохранённые документы слушателя', 'error', 5000, notify)
                return
            }
            openExtraction(selected.map(item => ({ fileID: Number(item.fileID), name: item.fileName, url: item.filePath })))
        } catch {
            if (!controller.signal.aborted && session === recognitionSessionRef.current) {
                notify && notifyOpen('Не удалось подключиться к сервису распознавания документов', 'error', 5000, notify)
            }
        } finally {
            if (recognitionRequestRef.current === controller) {
                recognitionRequestRef.current = null
                setIsRecognitionLoading(false)
            }
        }
    }
    /** Функция по скачиванию файлов слушателя */
    const handlerDownloadFiles = (fileID?: number) => {
        let selectedFiles: IFilesList[] = []

        if (currentCardMode.value === 'new') return notify && notifyOpen(`Не возможно скачать файл или файлы, т.к. они еще не загружены на сервер.`, 'warning', 1500, notify)

        if (fileID) { selectedFiles = filesList.value.filter(file => file.fileID === fileID) } 
        else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        for (const file of selectedFiles) {
            const link = document.createElement('a') // Создание ссылки и программное скачивание файла

            link.href = file.filePath
            link.download = file.fileName
            document.body.appendChild(link)

            link.click()
            document.body.removeChild(link)
        }
    }
    /** Функция по удалению файлов слушателя */
    const handlerDeleteFiles = async (fileID?: number) => {
        if (recognitionRequestRef.current || extractionFiles.length || savingRef.current || saveGuard.isBlocked) return
        let selectedFiles: IFilesList[] = []

        if (fileID) { selectedFiles = filesList.value.filter(file => file.fileID === fileID) } 
        else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        if (selectedFiles.some(file => uncertainDraftUploadsRef.current.has(file.fileName))) {
            notify && notifyOpen('Загрузка документов не подтверждена. Повторите распознавание этих файлов перед сохранением или удалением.', 'warning', 7000, notify)
            return
        }

        switch (currentCardMode.value) {
            case 'new': {
                const controller = new AbortController()
                const session = recognitionSessionRef.current
                recognitionRequestRef.current = controller
                setIsRecognitionLoading(true)
                const removed = new Set<string>()
                try {
                    for (const file of selectedFiles) {
                        const stagedID = stagedFilesRef.current.get(file.fileName)
                        if (stagedID && draftIDRef.current) {
                            const response = await deleteListenerDraftFile(draftIDRef.current, stagedID, controller.signal)
                            if (controller.signal.aborted || session !== recognitionSessionRef.current) return
                            if (response.status !== 200) throw new Error('draft_delete_failed')
                            stagedFilesRef.current.delete(file.fileName)
                        }
                        removed.add(file.fileName)
                    }
                } catch {
                    if (!controller.signal.aborted && session === recognitionSessionRef.current) notify && notifyOpen('Не удалось удалить документ из черновика. Повторите позже.', 'error', 5000, notify)
                } finally {
                    if (session === recognitionSessionRef.current) {
                        filesList.setValue(filesList.value.filter(file => !removed.has(file.fileName)))
                        filesListSendToSrv.setValue(filesListSendToSrv.value.filter(file => !removed.has(file.name)))
                    }
                    if (recognitionRequestRef.current === controller) {
                        recognitionRequestRef.current = null
                        setIsRecognitionLoading(false)
                    }
                }
                break
            }
            case 'edit': { // При редактировании слушателя
                for (const file of selectedFiles) {
                    await actionSetListenerDeleteFiles(currentCardID.value, file.fileID).finally(() => {
                        actionGetListenerFiles(currentCardID.value).then((files) => {
                            filesList.setValue(files.map((item) => ({
                                fileID: item.listenerFileID,
                                fileName: item.fileName,
                                filePath: item.filePath,
                                fileSize: item.fileSize,
                                dateCreate: item.dateCreate,
                                isSelected: false
                            })))
                        })
                    })
                } break
            }
        }
    }



    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = (isClosedModal: boolean) => {
        if (recognitionRequestRef.current || extractionFiles.length || savingRef.current || isLoadingCard || saveGuard.isBlocked) return
        if (uncertainDraftUploadsRef.current.size) {
            notify && notifyOpen('Загрузка документов не подтверждена. Повторите распознавание этих файлов перед сохранением или удалением.', 'warning', 7000, notify)
            return
        }
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'citizenship', value: txtCitizenship.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Гражданство слушателя, обязательно должно быть выбрано.'},
            {fieldName: 'snils', value: txtSnils.value, validType: (clientSnilsAuto.value || txtCitizenshipID.value !== 185) ? 'notEmpty' : 'snils', isRequired: true, errorMessage: 'СНИЛС слушателя не корректный.'},
            {fieldName: 'clientPhone', value: txtClientPhone.value, validType: 'phoneMask', isRequired: true, errorMessage: 'Номер телефона не корректный.'},
            {fieldName: 'clientEmail', value: txtClientEmail.value, validType: 'email', isRequired: false, errorMessage: 'Электронная почта не корректная.'},

            {fieldName: 'birthDate', value: txtBirthDate.value, validType: 'date', isRequired: true, errorMessage: 'Дата рождения не корректна или такой даты не существует.'},
            {fieldName: 'lastName', value: txtLastName, validType: 'notEmpty', isRequired: true, errorMessage: 'Фамилию слушателя обязательно нужно заполнить.'},
            {fieldName: 'firstName', value: txtFirstName, validType: 'notEmpty', isRequired: true, errorMessage: 'Имя слушателя обязательно нужно заполнить.'},
            {fieldName: 'fullNameFrom', value: txtFullNameFrom.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Ф.И.О. слушателя (от кого) обязательно нужно заполнить.'},
            {fieldName: 'sex', value: txtSex.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Пол слушателя, необходимо указать.'},
            
            {fieldName: 'passport', value: txtPassport.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Паспорт слушателя, необходимо заполнить.'},
            {fieldName: 'address', value: txtAddress.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Адрес прописки слушателя, необходимо заполнить.'},

            {fieldName: 'educationLevel', value: txtEducationLevel.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Уровень образования слушателя, необходимо выбрать.'},
            {fieldName: 'diplom', value: txtDiplom.value, validType: 'notEmpty', isRequired: txtEducationLevelID.value === 1 ? false : true, errorMessage: 'Документ об образовании слушателя, необходимо заполнить.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnumation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }

        if (validateData.length === 0) { // Сохраняем данные
            saveGuard.runSave(async () => {
                const savingDraftID = draftIDRef.current
                let isConfirmedRejection = false
                savingRef.current = true
                try {
                    const response = await actionSetListenerCard(currentCardID.value, {
                        actionForm: currentCardMode.value,
                        ...(savingDraftID ? { draftID: savingDraftID } : {}),

                        citizenshipID: Number(txtCitizenshipID.value),
                        snils: txtSnils.value,
                        phoneNumber: txtClientPhone.value,
                        email: txtClientEmail.value,

                        birthDate: txtBirthDate.value,
                        lastName: txtLastName,
                        firstName: txtFirstName,
                        secondName: txtSecondName,
                        fullNameFrom: txtFullNameFrom.value,
                        sex: txtSex.value,

                        passport: txtPassport.value,
                        registration: txtAddress.value,

                        educationLevelID: Number(txtEducationLevelID.value),
                        maidenName: txtLastNameDiplom.value,
                        diplom: txtDiplom.value,

                        comment: txtComment.value,
                        lkLogin: txtLogin.value.trim() || txtClientPhone.value.trim()
                    }, filesListSendToSrv.value.filter(file => !stagedFilesRef.current.has(file.name)))
                    isConfirmedRejection = Number.isInteger(response.status) && response.status >= 400 && response.status < 500
                        && ![408, 409, 499].includes(response.status)
                    return response
                } finally {
                    savingRef.current = false
                    // Closing during a save defers cleanup until a definite rejection; an unknown outcome may still create the listener.
                    if (isConfirmedRejection && savingDraftID && draftIDRef.current !== savingDraftID) {
                        void abandonListenerDraft(savingDraftID).catch(() => undefined)
                    }
                }
            }, {isNew: currentCardMode.value === 'new', getSavedID: response => response.listenerID}).then((res) => {
                if (res?.status === 200) {
                    const savedListenerID = Number(res.listenerID || currentCardID.value)
                    if (!Number.isSafeInteger(savedListenerID) || savedListenerID <= 0) {
                        notify && notifyOpen('Сервер не вернул идентификатор сохранённого слушателя.', 'error', 2000, notify)
                        return
                    }

                    draftIDRef.current = undefined
                    stagedFilesRef.current.clear()
                    uncertainDraftUploadsRef.current.clear()
                    notify && notifyOpen(res.warning || 'Данные успешно сохранены.', res.warning ? 'warning' : 'success', res.warning ? 7000 : 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal && !res.warning) {
                        handlerButtonOk && handlerButtonOk()
                        return
                    }

                    currentCardID.setValue(savedListenerID)
                    currentCardMode.setValue('edit')
                    listenerNumber.setValue(savedListenerID)
                    filesListSendToSrv.setValue([])
                    savedFormSnapshotRef.current = getCurrentFormSnapshot([])
                    savedNamesSignatureRef.current = createListenerNamesSignature(txtLastName, txtFirstName, txtSecondName)
                    isDisableBtnSave.setValue(true)

                    actionGetListenerCard(savedListenerID).then((listener) => {
                        if (listener) applyListenerCardData(listener)
                    })
                }
            })
        }
    }
    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnumation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)



    /** Проверка номера телефона на валидность, и проверка на дубль */
    const debouncePhone = useDebounce(listenerCheckPhoneNumber, 1500)
    const handleChangePhone = (phoneNumber: string) => {
        const resultPhoneMask = phoneMask(phoneNumber)

        txtLogin.setValue(resultPhoneMask.data)
        txtClientPhone.setValue(resultPhoneMask.data)
        
        debouncePhone(resultPhoneMask.data)
    }
    /** Проверка СНИЛС на дубль */
    const debounceSnils = useDebounce(listenerCheckSnils, 1500)
    const handleChangeSnils = (value: string) => {
        txtSnils.setValue(value)
        debounceSnils(value)
    }


    // Загрузка файлов на сервер
    const fileUploader = async (files: File[]) => {
        if (recognitionRequestRef.current || extractionFiles.length || savingRef.current || saveGuard.isBlocked || isLoadingCard) return
        const recognitionSession = recognitionSessionRef.current
        const maxSize = 104857600 // Максимально разрешенный размер файла для загрузки (100 MB)

        switch (currentCardMode.value) {
            case 'new': { // При создании слушателя
                const newFiles: IFilesList[] = files.reduce<IFilesList[]>((acc, item) => {
                    // Проверка на максимальный размер файла
                    if (item.size > maxSize) {
                        notify && notifyOpen(`Файл ${item.name}, превышает максимально допустимый размер в 100 мегабайт.`, 'error', 1500, notify)
                        return acc
                    }

                    // Проверка на уникальность имени файла
                    const isAlreadyAdded = filesList.value.some(existingFile => existingFile.fileName === item.name) || acc.some(existingFile => existingFile.fileName === item.name)
                    if (isAlreadyAdded) {
                        notify && notifyOpen(`Файл с именем ${item.name} уже добавлен в список.`, 'error', 1500, notify)
                        return acc
                    }

                    // Добавление нового файла
                    const newFile: IFilesList = {
                        fileID: Number(CommonService.generateDigits(10)),
                        fileName: item.name,
                        filePath: '',
                        fileSize: CommonService.getDefiningFileUnit(item.size),
                        dateCreate: new Date().toISOString(),
                        isSelected: false
                    }

                    acc.push(newFile)
                    return acc
                }, [])

                // Обновление списка файлов, добавляя новые файлы
                filesList.setValue([...filesList.value, ...newFiles])

                const newFilesOnly = newFiles.map(f => files.find(file => file.name === f.fileName)).filter(f => f) as File[]
                filesListSendToSrv.setValue([...filesListSendToSrv.value, ...newFilesOnly])
                break
            }

            case 'edit': { // При редактировании слушателя
                await actionSetListenerUploadFiles(currentCardID.value, files).finally(() => {
                    return actionGetListenerFiles(currentCardID.value).then((files) => {
                        if (recognitionSession !== recognitionSessionRef.current) return
                        filesList.setValue(files.map((item) => ({
                            fileID: item.listenerFileID,
                            fileName: item.fileName,
                            filePath: item.filePath,
                            fileSize: item.fileSize,
                            dateCreate: item.dateCreate,
                            isSelected: false
                        })))
                    })
                }).catch(() => {
                    if (recognitionSession === recognitionSessionRef.current) {
                        notify && notifyOpen('Не удалось загрузить или обновить список файлов. Проверьте список перед повторной загрузкой.', 'error', 5000, notify)
                    }
                    return false
                })
                break
            }
        }
    }

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardListener = {
        isOpen, cardMode: currentCardMode.value,
        isLoading: isLoadingCard, isRecognitionLoading, tabActive,
        isDisableBtnSave: {...isDisableBtnSave, value: isDisableBtnSave.value || saveGuard.isBlocked || isRecognitionLoading || extractionFiles.length > 0},
        filesList, listenerNumber, listenerDateCreate,  

        getCardPage, fileUploader, 
        handlerDownloadFiles, handlerDeleteFiles, handlerRecognizeFiles,
        handleBtnSave, handleBtnClose,
    }

    /** Свойства передаваемые во вкладку - основные данные */
    const propsToTabPrimary: ICardListenerTabPrimary = { 
        fieldsError, clientSnilsAuto,
        isAddressDisabled: !isOpen || isLoadingCard,
        txtCitizenshipID, txtCitizenship, txtSnils, txtClientPhone, txtClientEmail,
        txtLastName, txtFirstName, txtSecondName, txtBirthDate, txtFullNameFrom,
        txtPassport, txtAddress, txtEducationLevelID, txtEducationLevel, txtLastNameDiplom, txtDiplom,
        txtComment, txtSex,

        setTxtLastName, setTxtFirstName, setTxtSecondName,
        dictSelectorData, handleChangePhone, handlerSnilsAuto, handleChangeSnils, handlerPhoneGenerate,
    }

    /** Свойства передаваемые во вкладку - файлы */
    const propsToTabFiles: ICardListenerTabFiles = {
        filesList, handlerSelectedFiles, handlerSelectedFilesAll, fileUploader, handlerDownloadFiles, handlerDeleteFiles, handlerOpenModalPreview,
    }

    const registrationAddressContextRef = useRef({
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        address: txtAddress.value, files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    })
    registrationAddressContextRef.current = {
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        address: txtAddress.value, files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    }
    const extractionSession = recognitionSessionRef.current
    const onRegistrationAddress = useCallback((value: string, expectedAddress: string) => {
        const context = registrationAddressContextRef.current
        if (!context.isOpen || context.isLoadingCard || context.isSaveBlocked || savingRef.current
            || recognitionSessionRef.current !== extractionSession
            || context.cardID !== cardID || context.cardMode !== cardMode
            || context.listenerID !== currentCardID.value || context.listenerMode !== currentCardMode.value
            || activeExtractionRef.current !== extractionFiles || context.files !== extractionFiles || !extractionFiles.length
            || context.address !== expectedAddress || !value.trim()) return false

        context.address = value
        txtAddress.setValue(value)
        fieldsError.setValue(previous => ({ ...previous, address: false }))
        return true
    }, [cardID, cardMode, currentCardID.value, currentCardMode.value, extractionFiles, extractionSession, fieldsError.setValue, txtAddress.setValue])

    const snilsContextRef = useRef({
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        snils: txtSnils.value, files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    })
    snilsContextRef.current = {
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        snils: txtSnils.value, files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    }
    const onSnils = useCallback((value: string, expectedSnils: string) => {
        const context = snilsContextRef.current
        if (!context.isOpen || context.isLoadingCard || context.isSaveBlocked || savingRef.current
            || recognitionSessionRef.current !== extractionSession
            || context.cardID !== cardID || context.cardMode !== cardMode
            || context.listenerID !== currentCardID.value || context.listenerMode !== currentCardMode.value
            || activeExtractionRef.current !== extractionFiles || context.files !== extractionFiles || !extractionFiles.length
            || context.snils !== expectedSnils || !value.trim()) return false

        context.snils = value
        txtSnils.setValue(value)
        txtSnilsBuff.setValue(value)
        clientSnilsAuto.setValue(false)
        fieldsError.setValue(previous => ({ ...previous, snils: false }))
        return true
    }, [cardID, cardMode, currentCardID.value, currentCardMode.value, extractionFiles, extractionSession, fieldsError.setValue, txtSnils.setValue, txtSnilsBuff.setValue, clientSnilsAuto.setValue])

    const birthDateContextRef = useRef({
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        birthDate: txtBirthDate.value, files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    })
    birthDateContextRef.current = {
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        birthDate: txtBirthDate.value, files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    }
    const onBirthDate = useCallback((value: string, expectedBirthDate: string) => {
        const context = birthDateContextRef.current
        if (!context.isOpen || context.isLoadingCard || context.isSaveBlocked || savingRef.current
            || recognitionSessionRef.current !== extractionSession
            || context.cardID !== cardID || context.cardMode !== cardMode
            || context.listenerID !== currentCardID.value || context.listenerMode !== currentCardMode.value
            || activeExtractionRef.current !== extractionFiles || context.files !== extractionFiles || !extractionFiles.length
            || context.birthDate !== expectedBirthDate || !value.trim()) return false

        context.birthDate = value
        txtBirthDate.setValue(value)
        fieldsError.setValue(previous => ({ ...previous, birthDate: false }))
        return true
    }, [cardID, cardMode, currentCardID.value, currentCardMode.value, extractionFiles, extractionSession, fieldsError.setValue, txtBirthDate.setValue])

    const namesContextRef = useRef({
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        names: { lastName: txtLastName, firstName: txtFirstName, secondName: txtSecondName },
        files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    })
    namesContextRef.current = {
        isOpen, cardID, cardMode, listenerID: currentCardID.value, listenerMode: currentCardMode.value,
        names: { lastName: txtLastName, firstName: txtFirstName, secondName: txtSecondName },
        files: extractionFiles, isLoadingCard, isSaveBlocked: saveGuard.isBlocked,
    }
    const onNames = useCallback((value: RecognizedNames, expectedNames: ListenerNames) => {
        const context = namesContextRef.current
        const hasLastName = value.lastName !== undefined
        const hasFirstName = value.firstName !== undefined
        const hasSecondName = value.secondName !== undefined
        if (!context.isOpen || context.isLoadingCard || context.isSaveBlocked || savingRef.current
            || recognitionSessionRef.current !== extractionSession
            || context.cardID !== cardID || context.cardMode !== cardMode
            || context.listenerID !== currentCardID.value || context.listenerMode !== currentCardMode.value
            || activeExtractionRef.current !== extractionFiles || context.files !== extractionFiles || !extractionFiles.length
            || context.names.lastName !== expectedNames.lastName || context.names.firstName !== expectedNames.firstName
            || context.names.secondName !== expectedNames.secondName
            || (!hasLastName && !hasFirstName && !hasSecondName)
            || (hasLastName && !value.lastName?.trim()) || (hasFirstName && !value.firstName?.trim())
            || (hasSecondName && !value.secondName?.trim())) return false

        context.names = {
            lastName: hasLastName ? value.lastName! : context.names.lastName,
            firstName: hasFirstName ? value.firstName! : context.names.firstName,
            secondName: hasSecondName ? value.secondName! : context.names.secondName,
        }
        if (hasLastName) setTxtLastName(value.lastName!)
        if (hasFirstName) setTxtFirstName(value.firstName!)
        if (hasSecondName) setTxtSecondName(value.secondName!)
        if (hasLastName || hasFirstName) fieldsError.setValue(previous => ({
            ...previous, ...(hasLastName ? { lastName: false } : {}), ...(hasFirstName ? { firstName: false } : {}),
        }))
        return true
    }, [cardID, cardMode, currentCardID.value, currentCardMode.value, extractionFiles, extractionSession, fieldsError.setValue])

    return <>
        <CardListener {...propsToComponent} />

        {isOpen && extractionFiles.length > 0 && <ListenerDocumentExtraction
            listenerID={draftIDRef.current ?? currentCardID.value}
            files={extractionFiles}
            registrationAddress={txtAddress.value}
            onRegistrationAddress={onRegistrationAddress}
            snils={txtSnils.value}
            onSnils={onSnils}
            birthDate={txtBirthDate.value}
            onBirthDate={onBirthDate}
            names={{ lastName: txtLastName, firstName: txtFirstName, secondName: txtSecondName }}
            onNames={onNames}
            onClose={closeExtraction}
        />}

        <ModalConfirm {...modalConfirm.value} />
        <ModalNotice {...modalNotice.value} />
        <ModalPreview {...modalPreview.value} />
    </>
}
